// Route-module resolver (CR-W2a-35). `pnpm gen` lists every `apps/web/src/routes/<dir>/index.tsx` in
// `_generated/routes.ts`; each module exports `screens` (core screen id → component). The host asks for a screen id,
// the resolver loads the modules (once, in parallel) and hands back a React.lazy component for the first match.
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { ApiClient } from '../api/client.ts';

/**
 * What the host passes every screen: `api` + `permissions` (all that today's route modules declare in their own
 * `ScreenProps`) and the route's `params` + `navigate`. The last two are optional in the type on purpose: React class
 * and function components are checked both ways, so a module that declares only `api` + `permissions` stays
 * assignable; a screen that needs a record id or to open another screen declares them as optional props.
 */
export interface ScreenHostProps {
  readonly api: ApiClient;
  readonly permissions: ReadonlySet<string>;
  /** `:param` segments of the matched route, decoded (`/materials/fabrics/:id` → `{ id }`); `{}` on a list route. */
  readonly params?: Readonly<Record<string, string>>;
  /** Opens another route (same as a rail link). */
  readonly navigate?: (route: string) => void;
}

export type ScreenComponent = ComponentType<ScreenHostProps>;

export interface ScreenModule {
  readonly screens: Readonly<Record<string, ScreenComponent>>;
}

export interface ScreenModuleEntry {
  readonly dir: string;
  readonly load: () => Promise<ScreenModule>;
}

export interface ScreenResolver {
  /**
   * Lazy component for the first of `ids` that some module exports, or `missing(ids[0])` when none does. A failed
   * load is not cached: asking again after a failure loads the modules again (the host's "Try again").
   */
  component(ids: readonly string[]): LazyExoticComponent<ScreenComponent>;
}

export function createScreenResolver(
  modules: readonly ScreenModuleEntry[],
  missing: (screenId: string) => ScreenComponent,
): ScreenResolver {
  const loads = new Map<number, Promise<ScreenModule>>();
  const lazies = new Map<string, LazyExoticComponent<ScreenComponent>>();

  const loadEntry = (entry: ScreenModuleEntry, index: number): Promise<ScreenModule> => {
    const cached = loads.get(index);
    if (cached) return cached;
    const load = entry.load().catch((error: unknown) => {
      loads.delete(index);
      throw error;
    });
    loads.set(index, load);
    return load;
  };

  /** A module that fails to load only matters when the screen is not in one that did load. */
  async function find(ids: readonly string[]): Promise<ScreenComponent> {
    const [first = ''] = ids;
    const settled = await Promise.allSettled(modules.map(loadEntry));
    for (const id of ids) {
      for (const result of settled) {
        const found = result.status === 'fulfilled' ? result.value.screens[id] : undefined;
        if (found) return found;
      }
    }
    const failed = settled.find((r) => r.status === 'rejected');
    if (failed) throw (failed as PromiseRejectedResult).reason;
    return missing(first);
  }

  return {
    component(ids) {
      const key = ids.join('|');
      const cached = lazies.get(key);
      if (cached) return cached;
      const created = lazy(() =>
        find(ids).then(
          (component) => ({ default: component }),
          (error: unknown) => {
            lazies.delete(key);
            throw error;
          },
        ),
      );
      lazies.set(key, created);
      return created;
    },
  };
}
